import React, { useCallback, useEffect, useState } from 'react';

import { useTheme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { TravellerProfileFragment, useDefaultTravellerFieldsQuery } from '@websky/graphql';
import { DocumentInfoCard } from '../../../InfoCard/types';
import DocumentFieldsItem from './DocumentFieldsItem/DocumentFieldsItem';
import { useForm, useFormState } from 'react-final-form';
import { IconButton } from '../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { useIsMobile } from '../../../utils';
import { getCitizenships } from './DocumentFieldsItem/utils';
import DocumentCard from './DocumentCard/DocumentCard';
import AddDocumentModal from './AddDocumentModal/AddDocumentModal';
import SectionWrapper from '../SectionWrapper/SectionWrapper';
import { DocumentType } from '../../types';
import { DocumentFields } from '../../consts';

interface Props {
	traveller: TravellerProfileFragment;
	documents: DocumentInfoCard[];
	primaryCountryCodes?: string[];
	documentsSectionRef?: React.MutableRefObject<((index: number) => void) | undefined>;
}

type ModalMode = 'add' | 'edit';

const DocumentsSection: React.FC<Props> = ({ traveller, documents, primaryCountryCodes, documentsSectionRef }) => {
	const { DocumentsSection: css } = useTheme('TravellerForm');

	const { t: tTraveller } = useTranslation('Traveller');
	const { t: tPassenger } = useTranslation('Passenger');

	const form = useForm();
	const { values, submitFailed } = useFormState({
		subscription: {
			values: true,
			submitFailed: true
		}
	});
	const isMobile = useIsMobile();

	const { data: documentsValidations } = useDefaultTravellerFieldsQuery();
	const documentValidators = documentsValidations?.DefaultTravellerProfile?.documents?.[0];

	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalMode, setModalMode] = useState<ModalMode>('edit');
	const [documentSnapshot, setDocumentSnapshot] = useState<DocumentType | null>(null);

	const removeDocumentHandler = useCallback(
		(index: number) => {
			form.mutators.documentsMutator({
				type: 'remove',
				index
			});
		},
		[form]
	);

	const clearDocumentFields = useCallback(
		(index: number) => {
			DocumentFields.forEach(field => form.change(`documents[${index}].${field}`, undefined));
		},
		[form]
	);

	const openEditModal = useCallback(
		(index: number) => {
			const docValues = form.getState().values.documents?.[index];
			setDocumentSnapshot(docValues ? { ...docValues } : null);
			setEditingIndex(index);
			setModalMode('edit');
			setIsModalOpen(true);
		},
		[form]
	);

	useEffect(() => {
		documentsSectionRef.current = (index: number) => {
			if (!documentsSectionRef) {
				return;
			}

			if (isModalOpen) {
				return;
			}

			openEditModal(index);
		};

		return () => {
			documentsSectionRef.current = undefined;
		};
	}, [documentsSectionRef, isModalOpen, openEditModal]);

	const openAddModal = useCallback(() => {
		const newIndex = documents.length;

		form.mutators.documentsMutator({ type: 'add' });

		DocumentFields.forEach(field => {
			if (values?.[field]) {
				form.change(`documents[${newIndex}].${field}`, values[field]);
			}
		});

		if (values?.nationality) {
			form.change(`documents[${newIndex}].nationality`, values.nationality);
		}

		setEditingIndex(newIndex);
		setModalMode('add');
		setIsModalOpen(true);
	}, [documents.length, form, values]);

	const resetModalState = useCallback(() => {
		setIsModalOpen(false);
		setEditingIndex(null);
		setModalMode('edit');
	}, []);

	const handleCloseModal = useCallback(() => {
		if (modalMode === 'add' && editingIndex !== null) {
			removeDocumentHandler(editingIndex);
		} else if (modalMode === 'edit' && editingIndex !== null) {
			DocumentFields.forEach(field =>
				form.change(`documents[${editingIndex}].${field}`, documentSnapshot?.[field as keyof DocumentType])
			);
		}

		resetModalState();
	}, [editingIndex, modalMode, removeDocumentHandler, resetModalState, documentSnapshot, form]);

	const handleSaveModal = useCallback(() => {
		resetModalState();
	}, [resetModalState]);

	const addDocumentHandler = () => {
		form.mutators.documentsMutator({ type: 'add' });
	};

	const savedDocumentsCount = traveller.documents?.length ?? 0;

	const canRemoveDocument = (index: number) => {
		return index >= savedDocumentsCount || savedDocumentsCount > 1;
	};

	const isEdit = modalMode === 'edit';
	const onClearDocumentMobileModal = isEdit ? clearDocumentFields : undefined;

	if (isMobile) {
		const citizenShips = getCitizenships(traveller, primaryCountryCodes);

		const getCitizenshipLabel = (code: string) => {
			const found = citizenShips.find(c => c.value === code);

			return found ? tPassenger(found.label) : code;
		};

		const showDocumentsError = submitFailed && documents.length === 0;

		return (
			<SectionWrapper
				title={tTraveller('Documents')}
				error={showDocumentsError ? tTraveller('Please enter data') : undefined}
				headerButton={
					<IconButton className={css.documents__add} onClick={openAddModal} type="button">
						<CdnIcon icon={'Action/Add'} />
					</IconButton>
				}
			>
				{documents.length > 0 && (
					<div className={css.documentsSection}>
						<div className={css.documentsSection__fields}>
							{documents.map((document, index) => {
								return (
									<DocumentCard
										key={index}
										document={document}
										index={index}
										docTypeLabel={document.docType ? tPassenger(document.docType) : ''}
										citizenshipLabel={
											document.citizenship ? getCitizenshipLabel(document.citizenship) : undefined
										}
										onEdit={openEditModal}
										onRemove={canRemoveDocument(index) ? removeDocumentHandler : undefined}
									/>
								);
							})}
						</div>
					</div>
				)}

				{editingIndex !== null && (
					<AddDocumentModal
						isOpen={isModalOpen}
						index={editingIndex}
						validators={documentValidators}
						traveller={traveller}
						primaryCountryCodes={primaryCountryCodes}
						onClose={handleCloseModal}
						onSave={handleSaveModal}
						onClear={onClearDocumentMobileModal}
						isEdit={isEdit}
					/>
				)}
			</SectionWrapper>
		);
	}

	const documentFieldsItems = documents.map((document, index) => {
		return (
			<DocumentFieldsItem
				key={index}
				id={index}
				document={document}
				validators={documentValidators}
				traveller={traveller}
				primaryCountryCodes={primaryCountryCodes}
				onRemove={canRemoveDocument(index) ? removeDocumentHandler : undefined}
				onClear={!canRemoveDocument(index) ? clearDocumentFields : undefined}
			/>
		);
	});

	return (
		<div className={css.documentsSection}>
			<div className={css.documentsSection__fields}>
				{!!documents.length ? (
					documentFieldsItems
				) : (
					<div className={css.documentsSection__emptyWarning}>{tTraveller('You need to add a document')}</div>
				)}
			</div>

			<div className={css.documentsSection__controls}>
				<div className={css.documentsSection__addFields} onClick={addDocumentHandler}>
					<IconButton
						classes={{
							root: css.buttonAdd
						}}
					>
						<CdnIcon icon={'Action/Add'} />
					</IconButton>

					{tTraveller('Add document')}
				</div>
			</div>
		</div>
	);
};

export default DocumentsSection;
